import { useEffect, useMemo, useState } from 'react';
import { ArrowLeftCircleIcon, ArrowRightCircleIcon } from '@heroicons/react/24/outline';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ADMIN_ROUTES_CLIENT,
  EMPLOYEE_ROUTES,
  ROUTES,
  ROUTES_CLIENT,
  SUPER_ADMIN_COMPANY_CLIENT_ROUTES,
  SUPER_ADMIN_COMPANY_ROUTES,
  SUPER_ADMIN_ROUTES,
} from '../common/constants/routes';
import { classNames } from '../common/utils/utils';
import { IRoute } from '../common/interfaces/route.interface';
import MenuLink from './MenuLink';
import i18n from '../common/config/i18n';
import MenuItem from './MenuItem';
import { useUserProfile } from '../contexts/user/UserContext';
import { UserRoleEnum } from '../common/interfaces/user-profile.interface';
import React from 'react';

const SideMenu = ({ outputIsNarrot }: { outputIsNarrot: (isNarrow: boolean) => void }) => {
  const navigate = useNavigate();
  const { clientId, companyId } = useParams();
  const location = useLocation();
  const [isNarrow, setIsNarrow] = useState<boolean>(false);
  const { user } = useUserProfile();

  const userRoutes = useMemo(() => {
    if (user?.role !== UserRoleEnum.SUPER_ADMIN) {
      if (clientId && user?.role !== UserRoleEnum.EMPLOYEE) {
        return ADMIN_ROUTES_CLIENT(clientId);
      } else if (clientId) {
        return ROUTES_CLIENT(clientId);
      } else if (user?.role === UserRoleEnum.EMPLOYEE) {
        return EMPLOYEE_ROUTES;
      } else {
        return ROUTES;
      }
    } else {
      if (clientId && companyId) {
        return SUPER_ADMIN_COMPANY_CLIENT_ROUTES(companyId, clientId);
      } else if (companyId) {
        return SUPER_ADMIN_COMPANY_ROUTES(companyId);
      }
      return SUPER_ADMIN_ROUTES;
    }
  }, [user?.role, companyId, clientId]);

  const [currentMenuItem, setCurrentMenuItem] = useState<IRoute>(userRoutes[0]);

  useEffect(() => {
    const splitLocations = location.pathname.split('/');
    const activeRoute = userRoutes
      .slice()
      .reverse()
      .find((route) => {
        const lastFromHref = route.href.split('/').pop();
        if (lastFromHref) {
          return splitLocations.includes(lastFromHref || '');
        }
      });

    if (activeRoute) setCurrentMenuItem(activeRoute as IRoute);
  }, [location]);

  const onMenuItemClick = (item: IRoute) => {
    setCurrentMenuItem(item);
    navigate(`${item.href}`);
  };

  return (
    <nav
      className={classNames(
        isNarrow ? 'w-[5.5rem]' : 'w-[16.5rem]',
        'transition-width duration-300 ease-out p-6 pt-10 space-y-4 bg-gray-900 rounded-xl cursor-pointer select-none h-fit sticky top-5',
      )}
      aria-label="Sidebar"
    >
      {userRoutes.map((item: IRoute) => (
        <React.Fragment key={item.id}>
          <MenuItem
            key={item.id}
            item={item}
            activeRoute={currentMenuItem}
            isNarrow={isNarrow}
            onClick={onMenuItemClick}
          />
          {item.lineAfter && <div className="border-t border-gray" />}
        </React.Fragment>
      ))}
      <div className="pt-60 space-y-4">
        <MenuLink
          item={{
            id: -1,
            name: i18n.t('side_menu:collapse'),
            icon: isNarrow ? ArrowRightCircleIcon : ArrowLeftCircleIcon,
            href: '',
          }}
          active={false}
          isNarrow={isNarrow}
          onClick={() => {
            outputIsNarrot(!isNarrow);
            return setIsNarrow(!isNarrow);
          }}
        />
      </div>
    </nav>
  );
};

export default SideMenu;
